import styled, { css } from 'styled-components';
import { VideoPlayer } from 'src/components';
import { Color } from 'src/utils/theme';

export const ContentOuterContainer = styled.div(
  ({ theme }) => css`
    background: ${theme.co.primary99};
    min-height: 100dvh;
  `,
);

export const ContentInnerContainer = styled.div(
  ({ theme }) => css`
    background: ${theme.co.primary99};
    min-height: 100dvh;
    max-width: ${theme.sz.xl};
    padding: ${theme.sz.s6};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s6};
    margin: auto;

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s4};
      gap: ${theme.sz.s4};
    }
  `,
);

export const Columns = styled.div<{ $hasVideo: boolean }>(
  ({ theme, $hasVideo }) => css`
    display: grid;
    grid-template-columns: ${$hasVideo ? '1fr 1fr' : `minmax(0, ${theme.sz.md})`};
    justify-content: center;
    align-items: stretch;
    gap: ${theme.sz.s6};
    width: 100%;

    @media (max-width: ${theme.sz.lg}) {
      grid-template-columns: 1fr;
      gap: ${theme.sz.s4};
    }
  `,
);

export const Stage = styled.div(
  ({ theme }) => css`
    position: relative;
    overflow: hidden;
    border-radius: ${theme.bo.medium};
    background: ${theme.co.primary90};
    aspect-ratio: 1;

    /* Stacked on small screens, a square clip pushes the CTA below the fold. */
    @media (max-width: ${theme.sz.lg}) {
      aspect-ratio: 4 / 3;
    }
  `,
);

export const Clip = styled(VideoPlayer)(
  () => css`
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      border-radius: 0;
      object-fit: cover;
    }
  `,
);

export const Phrase = styled.p(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    position: absolute;
    bottom: ${theme.sz.s4};
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100% - ${theme.sz.s8});
    margin: ${theme.sz.s0};
    padding: ${theme.sz.s2} ${theme.sz.s4};
    border-radius: ${theme.bo.round};
    background: ${theme.co.neutral20};
    color: ${theme.co.neutral99};
    text-align: center;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

export const Card = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${theme.sz.s5};
    padding: ${theme.sz.s8};
    border-radius: ${theme.bo.medium};
    background: ${theme.co.neutral99};
    box-shadow: ${theme.sh.s2};

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s5};
      gap: ${theme.sz.s4};
    }
  `,
);

export const Title = styled.h1(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.m};
    color: ${theme.co.secondary0};
    margin: ${theme.sz.s0};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.m};
    }
  `,
);

export const Divider = styled.hr(
  ({ theme }) => css`
    width: 100%;
    height: ${theme.bo.thinWidth};
    margin: ${theme.sz.s0};
    border: none;
    background: ${theme.co.neutral90};
  `,
);

export const PlanSection = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s1};
  `,
);

export const PlanRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${theme.sz.s4};
    ${theme.typography.heading.desktop.xs};
    color: ${theme.co.secondary0};
  `,
);

export const PlanDescription = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary50};
    margin: ${theme.sz.s0};
  `,
);

export const SocialProof = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s3};
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary20};
  `,
);

export const Avatars = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    padding-left: ${theme.sz.s2};
  `,
);

export const Avatar = styled.span<{ $color: Color }>(
  ({ theme, $color }) => css`
    ${theme.typography.heading.desktop.xxxs};
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s7};
    height: ${theme.sz.s7};
    margin-left: -${theme.sz.s2};
    border: ${theme.bo.mediumWidth} solid ${theme.co.neutral99};
    border-radius: ${theme.bo.elliptical};
    background: ${theme.co[$color]};
    color: ${theme.co.neutral99};
  `,
);

export const ButtonContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s2};
    width: 100%;
    max-width: ${theme.sz.s};
  `,
);

export const FreeLink = styled.button(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    display: block;
    width: 100%;
    padding: ${theme.sz.s2} ${theme.sz.s0};
    border: none;
    background: none;
    color: ${theme.co.secondary50};
    font-weight: 500;
    text-align: center;
    cursor: pointer;
  `,
);
